import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { Mail, Loader2, Sparkles, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Iter215 · Modal para reenviar al CFO un email de actualización generado por IA
 * con el diff de la oferta desde el último email enviado.
 *
 * Props:
 *   open, offerId, onClose, onSent
 */
export function CfoUpdateModal({ open, offerId, onClose, onSent }) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null); // {has_changes, changes, subject, body_html, requires_reapproval, prev_sent_at}
  const [error, setError] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [sending, setSending] = useState(false);

  const fetchPreview = useCallback(async () => {
    if (!offerId) return;
    setLoadingPreview(true); setError("");
    try {
      const { data } = await axios.post(`${API}/offers/${offerId}/cfo-update-preview`);
      setPreview(data);
      setSubject(data.subject || "");
      setBodyHtml(data.body_html || "");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error generando la previsualización";
      setError(msg);
    } finally {
      setLoadingPreview(false);
    }
  }, [offerId]);

  useEffect(() => {
    if (open) {
      setPreview(null); setError(""); setSubject(""); setBodyHtml("");
      fetchPreview();
    }
  }, [open, fetchPreview]);

  const handleSend = async () => {
    if (!subject.trim() || !bodyHtml.trim()) {
      toast.error("Asunto y cuerpo son obligatorios");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/offers/${offerId}/cfo-update-send`, {
        subject: subject.trim(),
        body_html: bodyHtml.trim(),
      });
      if ((data.sent || 0) > 0) {
        toast.success(`Email enviado a ${data.sent}/${data.cfo_count} CFO`);
        onSent && onSent();
        onClose && onClose();
      } else {
        toast.error(`No se envió a ninguno. Errores: ${(data.errors || []).join("; ")}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Error enviando");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="cfo-update-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-4 flex items-center gap-3">
          <div className="p-2 bg-white/15 rounded-lg"><Sparkles size={18} /></div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold">Actualizar CFO con cambios</h3>
            <p className="text-[12px] opacity-85">Claude Sonnet 4.6 redacta el email con el diff desde el último envío</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none" data-testid="cfo-update-close">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {loadingPreview && (
            <div className="flex flex-col items-center justify-center py-14 text-gray-500" data-testid="cfo-update-loading">
              <Loader2 size={32} className="animate-spin mb-3 text-indigo-600" />
              <p className="text-sm">Comparando cambios y redactando email…</p>
            </div>
          )}

          {!loadingPreview && error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="cfo-update-error">
              <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          {!loadingPreview && preview && !preview.has_changes && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid="cfo-update-no-changes">
              <CheckCircle size={28} className="text-emerald-600 mx-auto mb-2" />
              <p className="text-sm font-semibold text-emerald-800">Sin novedades para el CFO</p>
              <p className="text-xs text-emerald-700 mt-1">
                No hay cambios desde el último email enviado
                {preview.prev_sent_at ? ` el ${String(preview.prev_sent_at).slice(0, 16).replace("T", " ")}` : ""}.
              </p>
            </div>
          )}

          {!loadingPreview && preview && preview.has_changes && (
            <>
              {/* Lista compacta de cambios detectados */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="cfo-update-diff">
                <div className="text-[11px] uppercase font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  Cambios detectados ({preview.changes.length})
                  {preview.requires_reapproval && (
                    <span className="ml-auto bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full text-[10px] font-bold" data-testid="cfo-update-reapproval">
                      ⚠ requiere re-aprobación
                    </span>
                  )}
                </div>
                <ul className="space-y-1 text-[12px]">
                  {preview.changes.map((c, i) => (
                    <li key={i} className="flex flex-wrap items-baseline gap-1.5">
                      <span className="font-semibold text-gray-800">{c.label}:</span>
                      <span className="text-gray-500 line-through text-[11px]">{String(c.before ?? "").slice(0, 80)}</span>
                      <span className="text-gray-400">→</span>
                      <span className="text-emerald-700 font-mono">{String(c.after ?? "").slice(0, 80)}</span>
                      {c.delta_pct && <span className="text-[10px] text-amber-700 font-semibold">({c.delta_pct})</span>}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Editor de asunto */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Asunto</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
                  data-testid="cfo-update-subject"
                />
              </div>

              {/* Editor de body (HTML) */}
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 flex items-center justify-between">
                  <span>Cuerpo del email (HTML editable)</span>
                  <button
                    type="button"
                    onClick={fetchPreview}
                    className="text-[10px] text-indigo-600 hover:text-indigo-800 font-normal"
                    data-testid="cfo-update-regenerate"
                    title="Regenerar borrador con IA"
                  >
                    ↻ Regenerar con IA
                  </button>
                </label>
                <textarea
                  rows={10}
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:border-indigo-400 resize-y"
                  data-testid="cfo-update-body"
                />
              </div>

              {/* Preview renderizado */}
              <div>
                <div className="text-xs font-semibold text-gray-600 mb-1">Previsualización</div>
                <div
                  className="border border-gray-200 rounded-lg p-4 bg-white text-sm max-h-56 overflow-y-auto prose prose-sm"
                  data-testid="cfo-update-preview-rendered"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              </div>
            </>
          )}
        </div>

        <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between gap-3 bg-gray-50">
          <div className="text-[11px] text-gray-500">
            Se enviará a: <b>gferreresd@valorizasm.com</b>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-white"
              data-testid="cfo-update-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={handleSend}
              disabled={sending || loadingPreview || !preview?.has_changes}
              className="px-5 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="cfo-update-send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {sending ? "Enviando…" : "Enviar al CFO"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
